<template>
    <div class="MentalHygieneView">
        <h1>{{ $t('title') }}</h1>
        <h2>{{ today }}</h2>

        <div class="section">
            <div class="section-title">
                {{ $t('startOfDay') }}
            </div>
            <div class="item">
                <div class="icon daily-joy" />
                <div class="title">
                    {{ $t('dailyJoy') }}
                </div>
                <div class="content joys">
                    <div v-for="(joy, i) in joys" :key="i">
                        <input
                            ref="joys"
                            v-model="joy.joy"
                            type="text"
                            :placeholder="$t('dailyJoyPlaceholder')"
                            @keyup="updateJoy(joy)"
                            @keyup.enter="createJoy()"
                        />
                        <span class="button" @click="deleteJoy(joy, i)">remove</span>
                    </div>
                </div>
                <div class="actions">
                    <div class="add-joy --global-clickable" @click="createJoy()"> + {{ $t('addJoy') }} </div>
                </div>
            </div>
            <!-- item -->

            <div class="item">
                <div class="icon daily-frogs" />
                <div class="title">
                    {{ $t('dailyFrogs') }}
                </div>
                <div class="content frogs">
                    <div v-for="(frog, i) in frogs" :key="i">
                        <input
                            ref="frogs"
                            v-model="frog.frog"
                            type="text"
                            :placeholder="$t('frogPlaceholder')"
                            @keyup="updateFrog(frog)"
                            @keyup.enter="createFrog()"
                        />
                        <span class="button" @click="deleteFrog(frog, i)">remove</span>
                    </div>
                </div>
                <div class="actions">
                    <div class="add-frog --global-clickable" @click="createFrog()"> + {{ $t('addFrog') }} </div>
                </div>
            </div>
        </div>

        <div class="section">
            <div class="section-title">
                {{ $t('duringDay') }}
            </div>
            <div class="item">
                <div class="title">
                    {{ $t('focusChecks') }}
                </div>
                <div class="content">
                    <span v-for="n in maxRating" :key="n">
                        <span
                            class="star --global-clickable"
                            :class="{ active: focusRating >= n }"
                            @click="createFocusCheck(n)"
                        >
                            *
                        </span>
                    </span>
                </div>
            </div>
        </div>

        <div class="section">
            <div class="section-title">
                {{ $t('endOfDay') }}
            </div>

            <div class="item">
                <div class="title">
                    {{ $t('nextDayPlan') }}
                </div>
                <div class="subtitle">
                    {{ $t('nextDayPlanSubtitle') }}
                </div>
                <div class="actions">
                    <div class="--global-clickable button" @click="doneDayPlan()"> {{ $t('done') }} {{ dayPlan }} </div>
                </div>
            </div>
        </div>

        <div class="item">
            <div class="title">
                {{ $t('learningDiary') }}
            </div>
            <div class="content column">
                <div v-for="(item, i) of diaryToday" :key="i" class="form-group">
                    <label v-if="item.connotation === 'POSITIVE'">:)</label>
                    <label v-if="item.connotation === 'NEGATIVE'">:(</label>
                    <input
                        v-model="item.item"
                        type="text"
                        :placeholder="
                            $t(item.connotation === 'POSITIVE' ? 'positivePlaceholder' : 'negativePlaceholder')
                        "
                        @keyup="createOrUpdateLearningDiary(item)"
                    />
                </div>
                <div class="actions" />
            </div>
        </div>
        <div class="diary item">
            <div class="title">
                {{ $t('learningDiary') }}
            </div>
            <div v-for="(value, name) in diaries" :key="name" class="day-diary">
                <div class="day">
                    {{ dateFormat(name) }}
                </div>
                <div v-for="item in value" :key="item.nodeId">
                    <span v-if="item.connotation === 'POSITIVE'">:)</span>
                    <span v-if="item.connotation === 'NEGATIVE'">:(</span>
                    {{ item.item }}
                </div>
            </div>
        </div>
    </div>
</template>

<translations>
title: My Mental Hygiene
title_no: Min Mentalhygiene

startOfDay: Start of day
startOfDay_no: Starten av dagen

dailyJoy: Daily Joy
dailyJoy_no: Daglig Glede

dailyJoyPlaceholder: What are you looking forward to today?
dailyJoyPlaceholder_no: Hva gleder du deg til i dag?

addJoy: Add more joy
addJoy_no: Legg til mer glede

dailyFrogs: Daily frogs
dailyFrogs_no: Dagens frosker

frogPlaceholder: Frogs are the tasks we don't like, but have to do.
frogPlaceholder_no: Frosker er oppgaver vi ikke liker, men må gjøre

addFrog: Add frog
addFrog_no: Legg til frosk

duringDay: During the day
duringDay_no: I løpet av dagen

focusChecks: Focus check
focusChecks_no: Fokussjekk

endOfDay: End of the day
endOfDay_no: Slutten av dagen

nextDayPlan: Plan your next day
nextDayPlan_no: Planlegg morgendagen

nextDayPlanSubtitle: Planning your next will help you get the edge
nextDayPlanSubtitle_no: Planlegg morgendagen hjelper deg å holde blalba

done: I'm done
done_no: Ferdig

learningDiary: Learning Diary
learningDiary_no: Læringsdagbok

positivePlaceholder: One positive thing you learned today
positivePlaceholder_no: En positiv ting du lærte i dag

negativePlaceholder: An improvement point
negativePlaceholder_no: Forbedringspunkt

</translations>

<script>
import moment from 'moment';
import gql from 'graphql-tag';
import apolloClient from '@/apollo-client';
import debounce from 'lodash/debounce';
import groupBy from 'lodash/groupBy';

export default {
    components: {},
    data: () => {
        return {
            maxRating: 5,
            focusRating: 0,
            joys: [],
            frogs: [],
            dayPlan: false,
            diary: [],
            diaryToday: []
        };
    },
    computed: {
        userId() {
            return this.$store.getters['moduleAuth/profile'].id;
        },
        today() {
            return moment().format('LL');
        },
        diaries() {
            return groupBy(this.diary, d => d.date);
        }
    },

    mounted() {
        this.fetchAndPopulate();
    },
    methods: {
        dateFormat(d) {
            return moment(d).format('LL');
        },
        fetchAndPopulate() {
            const today = moment().format('YYYY-MM-D');
            apolloClient
                .query({
                    query: gql`
                        query($today: Date!) {
                            mentalHygieneJoysList {
                                joy
                                nodeId
                            }
                            mentalHygieneFrogsList {
                                frog
                                nodeId
                            }
                            mentalHygieneFocusChecksList(first: 1, orderBy: CREATED_AT_DESC) {
                                rating
                            }
                            mentalHygieneDayplansList(first: 1, condition: { date: $today }) {
                                done
                            }
                            mentalHygieneLearningDiariesList(orderBy: DATE_DESC, first: 300) {
                                date
                                nodeId
                                item
                                connotation
                            }
                        }
                    `,
                    variables: { today }
                })
                .then(result => {
                    this.joys = result.data.mentalHygieneJoysList;
                    this.frogs = result.data.mentalHygieneFrogsList;

                    const focusCheck = result.data.mentalHygieneFocusChecksList[0] || { rating: 0 };
                    this.focusRating = focusCheck.rating;

                    const dayPlan = result.data.mentalHygieneDayplansList[0] || { done: false };
                    this.dayPlan = dayPlan.done;

                    this.diary = result.data.mentalHygieneLearningDiariesList;
                    this.diaryToday = this.diary.filter(item => item.date === today);
                    // make sure we have min 2 positive and 1 negative
                    const positive = this.diaryToday.filter(item => item.connotation === 'POSITIVE').length;
                    for (let i = positive; i < 2; i++) {
                        this.diaryToday.push({ connotation: 'POSITIVE', item: '' });
                    }
                    const negative = this.diaryToday.filter(item => item.connotation === 'NEGATIVE').length;
                    for (let i = negative; i < 1; i++) {
                        this.diaryToday.push({ connotation: 'NEGATIVE', item: '' });
                    }
                });
        },
        createJoy() {
            apolloClient
                .mutate({
                    fetchPolicy: 'no-cache',
                    mutation: gql`
                        mutation($userId: String!, $joy: String!) {
                            createMentalHygieneJoy(input: { mentalHygieneJoy: { userId: $userId, joy: $joy } }) {
                                mentalHygieneJoy {
                                    nodeId
                                    joy
                                }
                            }
                        }
                    `,
                    variables: { userId: this.userId, joy: '' }
                })
                .then(result => {
                    this.joys.push(result.data.createMentalHygieneJoy.mentalHygieneJoy);
                    this.$nextTick(() => {
                        this.$refs.joys[this.$refs.joys.length - 1].focus();
                    });
                    // pass
                })
                .catch(e => this.handleApiError);
        },

        updateJoy: debounce(
            function (joy) {
                apolloClient
                    .mutate({
                        fetchPOlicy: 'no-cache',
                        mutation: gql`
                            mutation($id: ID!, $joy: String!) {
                                updateMentalHygieneJoyByNodeId(input: { patch: { joy: $joy }, nodeId: $id }) {
                                    mentalHygieneJoy {
                                        id
                                    }
                                }
                            }
                        `,
                        variables: { joy: joy.joy, id: joy.nodeId }
                    })
                    .catch(e => this.handleApiError);
            },
            500,
            { leading: true }
        ),

        deleteJoy(joy, index) {
            apolloClient
                .mutate({
                    mutation: gql`
                        mutation($id: ID!) {
                            deleteMentalHygieneJoyByNodeId(input: { nodeId: $id }) {
                                clientMutationId
                            }
                        }
                    `,
                    variables: { id: joy.nodeId }
                })
                .then(result => {
                    this.joys.splice(index, 1);
                })
                .catch(this.handleApiError);
        },

        createFrog() {
            apolloClient
                .mutate({
                    fetchPolicy: 'no-cache',
                    mutation: gql`
                        mutation($userId: String!, $frog: String!) {
                            createMentalHygieneFrog(input: { mentalHygieneFrog: { userId: $userId, frog: $frog } }) {
                                mentalHygieneFrog {
                                    nodeId
                                    frog
                                }
                            }
                        }
                    `,
                    variables: { userId: this.userId, frog: '' }
                })
                .then(result => {
                    this.frogs.push(result.data.createMentalHygieneFrog.mentalHygieneFrog);
                    this.$nextTick(() => {
                        this.$refs.frogs[this.$refs.frogs.length - 1].focus();
                    });
                    // pass
                })
                .catch(e => this.handleApiError);
        },

        updateFrog: debounce(
            function (frog) {
                apolloClient
                    .mutate({
                        fetchPOlicy: 'no-cache',
                        mutation: gql`
                            mutation($id: ID!, $frog: String!) {
                                updateMentalHygieneFrogByNodeId(input: { patch: { frog: $frog }, nodeId: $id }) {
                                    mentalHygieneFrog {
                                        id
                                    }
                                }
                            }
                        `,
                        variables: { frog: frog.frog, id: frog.nodeId }
                    })
                    .catch(e => this.handleApiError);
            },
            500,
            { leading: true }
        ),

        deleteFrog(frog, index) {
            apolloClient
                .mutate({
                    mutation: gql`
                        mutation($id: ID!) {
                            deleteMentalHygieneFrogByNodeId(input: { nodeId: $id }) {
                                clientMutationId
                            }
                        }
                    `,
                    variables: { id: frog.nodeId }
                })
                .then(result => {
                    this.frogs.splice(index, 1);
                })
                .catch(this.handleApiError);
        },

        createFocusCheck(rating) {
            this.focusRating = rating;
            apolloClient
                .mutate({
                    fetchPolicy: 'no-cache',
                    mutation: gql`
                        mutation($userId: String!, $rating: Int!) {
                            createMentalHygieneFocusCheck(
                                input: { mentalHygieneFocusCheck: { userId: $userId, rating: $rating } }
                            ) {
                                clientMutationId
                            }
                        }
                    `,
                    variables: { userId: this.userId, rating: this.focusRating }
                })
                .then(result => {
                    // pass
                })
                .catch(e => this.handleApiError);
        },

        createDiaryItem(item) {
            apolloClient
                .mutate({
                    fetchPolicy: 'no-cache',
                    mutation: gql`
                        mutation($userId: String!, $item: String!, $connotation: LearningDiaryConnotation!) {
                            createMentalHygieneLearningDiary(
                                input: {
                                    mentalHygieneLearningDiary: {
                                        userId: $userId
                                        item: $item
                                        connotation: $connotation
                                    }
                                }
                            ) {
                                mentalHygieneLearningDiary {
                                    nodeId
                                }
                            }
                        }
                    `,
                    variables: { userId: this.userId, item: item.item, connotation: item.connotation }
                })
                .then(result => {
                    item.nodeId = result.data.createMentalHygieneLearningDiary.mentalHygieneLearningDiary.nodeId;
                })
                .catch(this.handleApiError);
        },

        updateDiaryItem(item) {
            apolloClient
                .mutate({
                    fetchPolicy: 'no-cache',
                    mutation: gql`
                        mutation($nodeId: ID!, $item: String!) {
                            updateMentalHygieneLearningDiaryByNodeId(
                                input: { nodeId: $nodeId, patch: { item: $item } }
                            ) {
                                clientMutationId
                            }
                        }
                    `,
                    variables: { nodeId: item.nodeId, item: item.item, connotation: item.connotation }
                })
                .then(result => {
                    // pass
                })
                .catch(this.handleApiError);
        },

        createOrUpdateLearningDiary: debounce(
            function (item) {
                if (item.nodeId) {
                    this.updateDiaryItem(item);
                } else {
                    this.createDiaryItem(item);
                }
            },
            500,
            { leading: true }
        ),

        doneDayPlan() {
            if (this.dayPlan === true) {
                return;
            }
            this.dayPlan = true;
            apolloClient
                .mutate({
                    fetchPolicy: 'no-cache',
                    mutation: gql`
                        mutation($userId: String!, $done: Boolean!) {
                            createMentalHygieneDayplan(
                                input: { mentalHygieneDayplan: { userId: $userId, done: $done } }
                            ) {
                                clientMutationId
                            }
                        }
                    `,
                    variables: { userId: this.userId, done: this.dayPlan }
                })
                .then(result => {
                    // pass
                })
                .catch(e => this.handleApiError);
        },

        handleApiError(e) {
            console.error(e); // eslint-disable-line no-console
            const updateError = e.graphQLErrors.map(({ message }) => {
                return message;
            });
            console.error(updateError); // eslint-disable-line no-console
        }
    }
};
</script>

<style lang="scss" scoped>
.MentalHygieneView {
    background: #eee;
    color: black;
    overflow-y: auto;
}

.section {
    border-top: 1px dotted black;
    display: flex;
    flex-direction: column;
}

.item {
    background-color: white;
    flex: 2;
    padding: 10px;
    border-radius: 5px;
    margin: 5px;
}

.content {
    display: flex;
}

.joys,
.frogs {
    display: flex;
    flex-direction: column;
}

input {
    flex: 1;
}

.button {
    border-radius: 10px;
    border: 1px solid black;
    padding: 5px;
}

.section-title {
    flex: 1;
}

.form-group {
    display: flex;

    label {
        flex: 1;
    }

    input {
        flex: 5;
    }
}

.column {
    display: flex;
    flex-direction: column;
}

.star {
    font-size: 500%;
    &.active {
        color: gold;
    }
}
</style>
